import { useState, useRef, useEffect, useCallback } from 'react'
import SignaturePad from 'signature_pad'
import type { PointGroup } from 'signature_pad'
import { X, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Brain, Loader2 } from 'lucide-react'
import { HandwritingToolbar } from './HandwritingToolbar'
import { LatexText } from '@/components/ui/LatexText'
import type { HandwritingPadProps } from './types'

export function HandwritingPad({
  onChange,
  readOnly = false,
  penColor = '#000000',
  penWidth = 2,
  className,
  fullscreen = false,
  onClose,
  initialData,
  questionIndex,
  questionScore,
  questionStem,
  questionImages,
  onPrevQuestion,
  onNextQuestion,
  onExplain,
  onGrade,
  canGoPrev,
  canGoNext,
  isGrading,
}: HandwritingPadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const sigRef = useRef<SignaturePad | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const historyRef = useRef<PointGroup[][]>([])
  const replayingRef = useRef(false)
  const [color, setColor] = useState(penColor)
  const [width, setWidth] = useState(penWidth)
  const [shapeCount, setShapeCount] = useState(0)
  const [stemExpanded, setStemExpanded] = useState(true)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const onChangeRef = useRef(onChange)
  const onCloseRef = useRef(onClose)

  // Keep callback refs current
  useEffect(() => { onChangeRef.current = onChange }, [onChange])
  useEffect(() => { onCloseRef.current = onClose }, [onClose])

  // Handle close: pass stroke data back
  const handleClose = useCallback(() => {
    const sig = sigRef.current
    if (onCloseRef.current) {
      onCloseRef.current(sig && !sig.isEmpty() ? sig.toData() : undefined)
    }
  }, [])

  // Initialize SignaturePad
  useEffect(() => {
    const canvas = canvasRef.current!
    const container = containerRef.current!
    const sig = new SignaturePad(canvas, {
      penColor: color,
      minWidth: width * 0.5,
      maxWidth: width * 1.5,
      velocityFilterWeight: 0.7,
    })
    sigRef.current = sig

    // Set canvas pixel buffer size once
    const initCanvas = () => {
      const w = container.offsetWidth
      const h = container.offsetHeight
      if (w === 0 || h === 0) return false

      const ratio = Math.max(window.devicePixelRatio || 1, 1)
      canvas.width = Math.round(w * ratio)
      canvas.height = Math.round(h * ratio)
      const ctx = canvas.getContext('2d')!
      ctx.scale(ratio, ratio)
      return true
    }

    // Try to init immediately, or wait for layout
    if (!initCanvas()) {
      const observer = new ResizeObserver(() => {
        if (initCanvas()) {
          const data = sig.toData()
          if (data.length > 0) {
            replayingRef.current = true
            sig.fromData(data)
            replayingRef.current = false
          }
          observer.disconnect()
        }
      })
      observer.observe(container)
    }

    // Restore initial data if provided
    const initData = initialData as PointGroup[] | undefined
    if (initData && initData.length > 0) {
      replayingRef.current = true
      sig.fromData(initData)
      replayingRef.current = false
      historyRef.current = [initData]
      setShapeCount(initData.length)
    } else {
      historyRef.current = [sig.toData()]
    }

    // Listen for stroke end → push to history + debounce onChange
    const handleEnd = () => {
      if (replayingRef.current) return

      historyRef.current.push(sig.toData())
      setShapeCount(sig.toData().length)

      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
      debounceRef.current = setTimeout(() => {
        if (onChangeRef.current && !sig.isEmpty()) {
          // Create a copy with white background (transparent PNG not accepted by AI)
          const tmpCanvas = document.createElement('canvas')
          tmpCanvas.width = canvas.width
          tmpCanvas.height = canvas.height
          const ctx = tmpCanvas.getContext('2d')!
          ctx.fillStyle = '#ffffff'
          ctx.fillRect(0, 0, tmpCanvas.width, tmpCanvas.height)
          ctx.drawImage(canvas, 0, 0)
          tmpCanvas.toBlob((blob) => {
            if (blob) onChangeRef.current!(blob)
          }, 'image/png')
        }
      }, 500)
    }

    sig.addEventListener('endStroke', handleEnd)

    return () => {
      sig.removeEventListener('endStroke', handleEnd)
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
      sig.off()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Sync color to signature pad
  useEffect(() => {
    if (sigRef.current) {
      sigRef.current.penColor = color
    }
  }, [color])

  // Sync width to signature pad
  useEffect(() => {
    if (sigRef.current) {
      sigRef.current.minWidth = width * 0.5
      sigRef.current.maxWidth = width * 1.5
    }
  }, [width])

  // Sync readOnly state
  useEffect(() => {
    if (sigRef.current) {
      if (readOnly) {
        sigRef.current.off()
      } else {
        sigRef.current.on()
      }
    }
  }, [readOnly])

  const handleUndo = useCallback(() => {
    const sig = sigRef.current
    if (!sig || historyRef.current.length <= 1) return

    historyRef.current.pop()
    const prev = historyRef.current[historyRef.current.length - 1]
    sig.clear()
    if (prev.length > 0) {
      replayingRef.current = true
      sig.fromData(prev)
      replayingRef.current = false
    }
    setShapeCount(prev.length)
  }, [])

  const handleClear = useCallback(() => {
    if (!sigRef.current) return
    sigRef.current.clear()
    historyRef.current = [sigRef.current.toData()]
    setShapeCount(0)
  }, [])

  const handleExport = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const tmpCanvas = document.createElement('canvas')
    tmpCanvas.width = canvas.width
    tmpCanvas.height = canvas.height
    const ctx = tmpCanvas.getContext('2d')!
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, tmpCanvas.width, tmpCanvas.height)
    ctx.drawImage(canvas, 0, 0)
    const url = tmpCanvas.toDataURL('image/png')
    const a = document.createElement('a')
    a.href = url
    a.download = 'handwriting.png'
    a.click()
  }, [])

  // ── Toolbar wrapper (shared between fullscreen and inline) ─────

  const toolbar = (
    <HandwritingToolbar
      tool="draw"
      color={color}
      width={width}
      canUndo={shapeCount > 0}
      isEmpty={shapeCount === 0}
      onToolChange={() => {}}
      onColorChange={setColor}
      onWidthChange={setWidth}
      onUndo={handleUndo}
      onClear={handleClear}
      onExport={handleExport}
      showExport={!readOnly}
      disabled={readOnly}
    />
  )

  // ── Fullscreen overlay ──────────────────────────────────────────

  if (fullscreen) {
    return (
      <div className="fixed inset-0 z-50 bg-white flex flex-col">
        {/* Top bar: toolbar + close */}
        <div className="shrink-0 flex items-center gap-2 border-b border-slate-200 px-3 py-2">
          <div className="flex-1 flex justify-center overflow-x-auto">
            {toolbar}
          </div>
          {onClose && (
            <button
              type="button"
              onClick={handleClose}
              className="shrink-0 flex items-center gap-1 px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md transition-colors"
            >
              <X className="w-4 h-4" />
              收起作答
            </button>
          )}
        </div>

        {/* Canvas — fills remaining space */}
        <div
          ref={containerRef}
          className="flex-1 min-h-0 bg-white"
        >
          <canvas
            ref={canvasRef}
            className="block w-full h-full touch-none"
          />
        </div>

        {/* Bottom bar: question info + actions */}
        <div className="shrink-0 border-t border-slate-200 bg-white">
          {/* Collapsible question stem */}
          {questionStem && (
            <div className="border-b border-slate-100">
              <button
                type="button"
                className="w-full flex items-center justify-between px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                onClick={() => setStemExpanded(!stemExpanded)}
              >
                <span className="font-medium">
                  第 {(questionIndex ?? 0) + 1} 题
                  {questionScore ? ` · ${questionScore} 分` : ''}
                </span>
                {stemExpanded ? (
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                ) : (
                  <ChevronUp className="w-4 h-4 text-slate-400" />
                )}
              </button>
              {stemExpanded && (
                <div className="px-4 pb-3 text-sm text-slate-600 bg-slate-50/80 max-h-48 overflow-y-auto">
                  <LatexText className="whitespace-pre-wrap">{questionStem}</LatexText>
                  {questionImages && questionImages.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {questionImages.map((img, idx) => (
                        <img
                          key={idx}
                          src={img}
                          alt={`题目图片-${idx}`}
                          className="max-h-32 rounded border border-slate-200 object-contain"
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex items-center justify-between px-4 py-2.5">
            <div className="flex items-center gap-2">
              {onPrevQuestion && (
                <button
                  type="button"
                  onClick={onPrevQuestion}
                  disabled={!canGoPrev}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                  上一题
                </button>
              )}
              {onNextQuestion && (
                <button
                  type="button"
                  onClick={onNextQuestion}
                  disabled={!canGoNext}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  下一题
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              {onExplain && (
                <button
                  type="button"
                  onClick={onExplain}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 transition-colors"
                >
                  <Brain className="w-3.5 h-3.5" />
                  智能讲解
                </button>
              )}
              {onGrade && (
                <button
                  type="button"
                  onClick={onGrade}
                  disabled={isGrading || readOnly}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isGrading ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      批改中...
                    </>
                  ) : (
                    'AI 批改'
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  // ── Inline mode (for puzzle drafts etc.) ────────────────────────

  return (
    <div className={`flex flex-col h-full ${className ?? ''}`}>
      <div className="mb-2 flex justify-center shrink-0">
        {toolbar}
      </div>
      <div
        ref={containerRef}
        className="rounded-lg border border-slate-200 overflow-hidden bg-white flex-1 min-h-0"
      >
        <canvas
          ref={canvasRef}
          className="block w-full h-full touch-none"
        />
      </div>
    </div>
  )
}
